import { FC } from "react"
import { useTranslation } from "react-i18next"

import { ISummarySection } from "@/types"
import { IListItem } from "components/List/List"
import Summary from "components/Summary/Summary"
import AssetStatusTag from "components/AssetStatusTag/AssetStatusTag"
import useAssetFields, { AssetField } from "../../hooks/useAssetFields"
import { useAssetDataContext } from "./AssetDataContext"
import { AssetStep } from "pages/AssetPage/config"
import { StatusType } from "components/Status/Status"

interface IAssetDataBasicDataProps {

}

const AssetDataBasicData: FC<IAssetDataBasicDataProps> = () => {
    const { t } = useTranslation()
    const { asset, article, invalidFields, pinnable, placement, onEditClick, getEditLink } = useAssetDataContext()
    const { getFields } = useAssetFields({ asset, article, invalidFields, pinnable })

    const basicDataAssetIdentificationItems = getFields([
        AssetField.lmid,
        AssetField.serialNumber,
        AssetField.serialNumberMachine,
        AssetField.drawingNumber,
        AssetField.manufacturerCode,
        AssetField.inventoryNumber,
        ...placement === "sidebar" ? [AssetField.labelIds] : [],
    ])

    const basicDataLifeCycleItems: IListItem[] = getFields([
        AssetField.internalOrderNumber,
        AssetField.expectedToBeAvailableOn,
        AssetField.expectedToBeReturnedOn,
        AssetField.supplierOrderNumber,
    ])

    const endOfOwnershipItems: IListItem[] = getFields([
        AssetField.reasonForDisposal,
        AssetField.disposalDate,
        AssetField.disposalDescription,
    ])

    const subSections: ISummarySection<IListItem>[] = [
        {
            title: t('asset.assetIdentification'),
            items: basicDataAssetIdentificationItems,
        },
        {
            title: t('asset.lifeCycle'),
            toolbar: <AssetStatusTag status={asset.status} />,
            items: basicDataLifeCycleItems,
        },
        ...asset.disposal ? [{
            title: t('assetDisposal.endOfOwnershipSummary'),
            subtitle: placement === "sidebar" && asset.disposal.disposedByEmail
                ? t('assetDisposal.assetWasDisposedBy', { user: asset.disposal.disposedByEmail })
                : undefined,
            status: placement === "summary" ? {
                type: StatusType.error,
                message: t(asset.isPurchased ? 'assetDisposal.endOfOwnershipWarning' : 'assetDisposal.endOfHoldershipWarning')
            } : undefined,
            items: endOfOwnershipItems,
        }] : [],
    ]

    const summaryData: ISummarySection<IListItem>[] = [
        {
            title: t('asset.basicData'),
            items: [],
            subSections,
            editLink: getEditLink?.(AssetStep.basicData),
            onEditClick: onEditClick ? () => onEditClick(AssetStep.basicData) : undefined,
        }
    ]

    return (
        <Summary data={summaryData} />
    )
}

export default AssetDataBasicData
